import { z } from 'zod';
import { responseTypeSchema, defaultRatingScaleResponseType } from './responseTypeSchema';
import { ModuleInstanceConfigurationType, PsychologicalQuestionResponseType } from '@/apolloClient.types';

export const teamResilienceCategorySchema = z
    .object({
        category: z.string(),
        instructions: z.string().optional(),
        scaleResponseType: responseTypeSchema, // per-category scale
        questions: z.array(z.string()).min(1),
        subCategories: z.array(z.string().nullable()), // parallel to questions by index
    })
    .refine(v => v.subCategories.length === v.questions.length, {
        path: ['subCategories'],
        message: 'Subcategories must match questions 1:1',
    });

export const teamResilienceConfigurationFormSchema = z
    .object({
        moduleConfigurationType: z.nativeEnum(ModuleInstanceConfigurationType),
        numberOfQuestions: z.number().optional(),
        instructions: z.string().optional(),
        instructionMobile: z.string().optional(),
        reminders: z.string().optional(),
        showDemoVideo: z.boolean().optional().nullable(),
        categories: z.array(z.string()).min(1, { message: 'At least one category is required' }),
        subCategories: z.array(z.string()).optional().nullable(),
        questions: z.array(teamResilienceCategorySchema),
    })
    .optional();

export type TeamResilienceConfigurationFormData = z.infer<typeof teamResilienceConfigurationFormSchema>;

// Defaults
export const DefaultTeamResilienceQuestionCategory = {
    TeamResources: 'Team Resources',
    MinimizingFactors: 'Minimizing Factors',
    MendingEfforts: 'Mending Efforts',
    ResilienceManifestation: 'Resilience Manifestation',
    Stressors: 'Stressors',
};

export const DefaultTeamResourcesSubCategory = {
    Physical: 'Physical',
    Affective: 'Affective',
    Cognitive: 'Cognitive',
};

export const DefaultStressorsSubCategory = {
    Psychological: 'Psychological',
    External: 'External',
    Workload: 'Workload',
};

const minimizingFactorsScale = {
    responseType: PsychologicalQuestionResponseType.Likert,
    scoring: [
        { score: 1, label: 'Not At All' },
        { score: 2, label: 'A Little Extent' },
        { score: 3, label: 'To Some Extent' },
        { score: 4, label: 'To A Great Extent' },
        { score: 5, label: 'To A Very Great Extent' },
    ],
};

const mendingEffortsAndResilienceManifestationScale = {
    responseType: PsychologicalQuestionResponseType.Likert,
    scoring: [
        { score: 1, label: 'No Need To Demonstrate This Behavior' },
        { score: 2, label: 'Never Or Rarely' },
        { score: 3, label: 'Some Of The Time' },
        { score: 4, label: 'Most Of The Time' },
        { score: 5, label: 'Every Time' },
    ],
};

const stressorsScale = {
    responseType: PsychologicalQuestionResponseType.Likert,
    scoring: [
        { score: 1, label: 'Never' },
        { score: 2, label: 'Rarely' },
        { score: 3, label: 'Occasionally' },
        { score: 4, label: 'Frequently' },
        { score: 5, label: 'Constantly' },
    ],
};

const defaultInstructions =
    "1. For questions 1 to 12, choose the number that describes your team's current resource level by using the 1-100 rating scale.\n" +
    '2. For questions 13 onwards, rate each question as it relates to you and your team.';

export const defaultTeamResilienceConfiguration: NonNullable<TeamResilienceConfigurationFormData> = {
    moduleConfigurationType: ModuleInstanceConfigurationType.TeamResilience,
    numberOfQuestions: 55,
    instructions: defaultInstructions,
    instructionMobile: defaultInstructions,
    reminders: 'The only right answer is the honest one, so be true to yourself in your response.',
    showDemoVideo: false,
    categories: [
        DefaultTeamResilienceQuestionCategory.TeamResources,
        DefaultTeamResilienceQuestionCategory.MinimizingFactors,
        DefaultTeamResilienceQuestionCategory.MendingEfforts,
        DefaultTeamResilienceQuestionCategory.ResilienceManifestation,
        DefaultTeamResilienceQuestionCategory.Stressors,
    ],
    subCategories: [
        DefaultTeamResourcesSubCategory.Physical,
        DefaultTeamResourcesSubCategory.Affective,
        DefaultTeamResourcesSubCategory.Cognitive,
        DefaultStressorsSubCategory.Psychological,
        DefaultStressorsSubCategory.External,
        DefaultStressorsSubCategory.Workload,
    ],
    questions: [
        {
            category: DefaultTeamResilienceQuestionCategory.TeamResources,
            scaleResponseType: { ratingScale: defaultRatingScaleResponseType },
            questions: [
                'Rest',
                'Energy',
                'Physical Fitness',
                'Endurance',
                'Mutual Trust',
                'Morale',
                'Motivation',
                'Composure',
                'Task or mission-related knowledge',
                'Shared situational awareness',
                'Alertness',
                'Mental readiness',
            ],
            subCategories: [
                DefaultTeamResourcesSubCategory.Physical,
                DefaultTeamResourcesSubCategory.Physical,
                DefaultTeamResourcesSubCategory.Physical,
                DefaultTeamResourcesSubCategory.Physical,
                DefaultTeamResourcesSubCategory.Affective,
                DefaultTeamResourcesSubCategory.Affective,
                DefaultTeamResourcesSubCategory.Affective,
                DefaultTeamResourcesSubCategory.Affective,
                DefaultTeamResourcesSubCategory.Cognitive,
                DefaultTeamResourcesSubCategory.Cognitive,
                DefaultTeamResourcesSubCategory.Cognitive,
                DefaultTeamResourcesSubCategory.Cognitive,
            ],
        },
        {
            category: DefaultTeamResilienceQuestionCategory.MinimizingFactors,
            scaleResponseType: { likertScale: minimizingFactorsScale },
            questions: [
                'Been adequately staffed to handle future challenges?',
                'Been adequately resourced to manage future challenges?',
                'Been adequately trained to manage future challenges?',
                'Developed plans to handle different potential challenges?',
                'Developed strong working relationships with one another?',
                'Adequately assessed its preparedness for future challenges?',
            ],
            subCategories: [null, null, null, null, null, null],
        },
        {
            category: DefaultTeamResilienceQuestionCategory.MendingEfforts,
            scaleResponseType: { likertScale: mendingEffortsAndResilienceManifestationScale },
            questions: [
                'Adequately assessed its preparedness for future challenges?',
                'Worked through areas of conflict that may have developed between team members as a result of the challenges or stressors',
                'Communicated appreciation for helpful actions taken by team members when facing the challenges or stressors',
                'Helped individual team members who were affected by the challenging event or stressor',
                'Repaired relationships with people outside the team that might have been strained',
                'Thanked people outside the team for their help and support',
                'Identified how to address future challenges (for example: who will fill in or help out if X happens, how they will address future needs)',
                'Agreed how to follow up to ensure progress',
                'Made changes to processes, procedures, or resources',
                'Took actions to address deficits (for example: insufficient sleep, distrust)',
            ],
            subCategories: [null, null, null, null, null, null, null, null, null, null],
        },
        {
            category: DefaultTeamResilienceQuestionCategory.ResilienceManifestation,
            scaleResponseType: { likertScale: mendingEffortsAndResilienceManifestationScale },
            questions: [
                'Alerted one another to potential problems',
                'Communicated about situational changes (for example: mission, resources)',
                'Responded to challenges when they occurred',
                'Sought assistance',
                'Ensured all team members were comfortable speaking up',
                'Offered backup or support when team members needed help (for example: overloaded, competing)',
                'Maintained regular, necessary work routines while under stress',
                'Intentionally switched to and from “normal” mode to “emergency” mode',
                'Relied upon team members with the most relevant expertise',
                'Identified “what was not working” in managing a challenging situation and made real-time changes',
                'Monitored team members’ health',
            ],
            subCategories: [null, null, null, null, null, null, null, null, null, null, null],
        },
        {
            category: DefaultTeamResilienceQuestionCategory.Stressors,
            scaleResponseType: { likertScale: stressorsScale },
            questions: [
                'Information overload?',
                'Uncertainty or doubt?',
                'Lack of sleep',
                'Emotional stressors?',
                'Interpersonal conflicts or disagreements?',
                'Prolonged separation from family?',
                'Time pressures?',
                'Loud noises?',
                'Confined spaces?',
                'Heavy workload?',
                'Insufficient resources?',
                'Physical demand?',
                'Hazardous conditions?',
                'Staffing or skill shortage?',
                'Personnel changes?',
            ],
            subCategories: [
                DefaultStressorsSubCategory.Psychological,
                DefaultStressorsSubCategory.Psychological,
                DefaultStressorsSubCategory.Psychological,
                DefaultStressorsSubCategory.Psychological,
                DefaultStressorsSubCategory.Psychological,
                DefaultStressorsSubCategory.Psychological,
                DefaultStressorsSubCategory.External,
                DefaultStressorsSubCategory.External,
                DefaultStressorsSubCategory.External,
                DefaultStressorsSubCategory.Workload,
                DefaultStressorsSubCategory.Workload,
                DefaultStressorsSubCategory.External,
                DefaultStressorsSubCategory.External,
                DefaultStressorsSubCategory.Workload,
                DefaultStressorsSubCategory.Workload,
            ],
        },
    ],
};
